import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Form, Formik, FormikProps, useFormikContext } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { parseApiError } from 'shared/utils/apiValidationErrors';
import { AlertTriangle, Ban, Calculator, Percent, Receipt, UserRound } from 'lucide-react';
import {
  FraisTunnelDetailDto,
  ModeCalculTarification,
  ReferentialItemDto,
  TypeCommission,
  TypeEcheance,
  TypePrelevement,
} from 'api/funnel';
import { funnelReferentialsAPI, funnelsAPI } from 'services/swagger-api';
import { useDialog } from 'hooks/useDialog';
import { FormikDropdown } from 'components/forms/formik/FormikDropdown';
import { FormikInputText } from 'components/forms/formik/FormikInputText';
import { FormikSwitch } from 'components/forms/formik/FormikSwitch';
import { CheckboxComponent } from 'components/forms/Checkbox';
import { useAsyncResource } from 'back-office/shared/hooks/useAsyncResource';
import { useFeeBeneficiaryOptions } from '../hooks/useActorOptions';
import { useBankAccountOptions } from '../hooks/useBankAccountOptions';
import { ExpressionVariable } from '../components/expression/ExpressionBuilder';
import { ModalShell, ModalSection } from 'back-office/shared/components/ModalShell';
import {
  TYPE_COMMISSION_OPTIONS,
  TYPE_ECHEANCE_OPTIONS,
  TYPE_PRELEVEMENT_OPTIONS,
  isMontantTTCMode,
} from '../funnelEnums';
import {
  TarifFields,
  emptyTarifValues,
  extractTarifValues,
  prefixTarifValues,
  tarifFromFormValues,
  tarifToFormValues,
} from '../components/TarifFields';
import { formatNumberList, parseNumberList } from '../funnelValues';

interface FeeFormValues extends Record<string, unknown> {
  natureRemunerationId: string;
  tarifModeCalcul: ModeCalculTarification;
  tarifMontant: number | '';
  tarifExpression: string;
  useConnectedBroker: boolean;
  beneficiairePersonneId: string;
  compteBancaireId: string;
  typePrelevement: TypePrelevement;
  typeEcheance: TypeEcheance;
  garantiesExclues: string[];
  hasTaxe: boolean;
  taxeModeCalcul: ModeCalculTarification;
  taxeMontant: number | '';
  taxeExpression: string;
  typeCommission: TypeCommission;
  minimum: number | '';
  maximum: number | '';
  valeurDefaut: number | '';
  valeurs: string;
  displayedOnPricing: boolean;
}

const emptyToUndefined = (value: unknown, original: unknown) =>
  original === '' ? undefined : value;

const validationSchema = Yup.object().shape({
  natureRemunerationId: Yup.string().required('La nature du frais est requise'),
  beneficiairePersonneId: Yup.string().when('useConnectedBroker', {
    is: false,
    then: (schema) => schema.required('Le bénéficiaire est requis'),
    otherwise: (schema) => schema.nullable(),
  }),
  minimum: Yup.number().nullable().transform(emptyToUndefined),
  maximum: Yup.number()
    .nullable()
    .transform(emptyToUndefined)
    .test('max-gte-min', 'Le maximum doit être supérieur ou égal au minimum', function (maximum) {
      const { minimum } = this.parent as FeeFormValues;
      if (maximum === undefined || maximum === null || minimum === '') return true;
      return Number(maximum) >= Number(minimum);
    }),
});

const buildInitialValues = (fee: FraisTunnelDetailDto | null): FeeFormValues =>
  ({
    natureRemunerationId: fee?.natureRemunerationId ?? '',
    // Le moteur ne calcule pas le « Montant fixe TTC » : un nouveau frais part en HT.
    ...prefixTarifValues(
      'tarif',
      fee?.tarif ? tarifToFormValues(fee.tarif) : emptyTarifValues(ModeCalculTarification.MontantFixeHT)
    ),
    useConnectedBroker: fee?.isCourtierConnecte ?? false,
    beneficiairePersonneId: fee?.beneficiairePersonneId ?? '',
    compteBancaireId: fee?.compteBancaireId ?? '',
    typePrelevement: fee?.typePrelevement ?? TypePrelevement.AEcheance,
    typeEcheance: fee?.typeEcheance ?? TypeEcheance.ATerme,
    garantiesExclues: fee?.garantiesExclues ?? [],
    hasTaxe: Boolean(fee?.taxe),
    ...prefixTarifValues('taxe', fee?.taxe ? tarifToFormValues(fee.taxe) : emptyTarifValues()),
    typeCommission: fee?.typeCommission ?? TypeCommission.Slider,
    minimum: fee?.minimum ?? '',
    maximum: fee?.maximum ?? '',
    valeurDefaut: fee?._default ?? '',
    valeurs: formatNumberList(fee?.valeurs),
    // Un nouveau frais s'affiche par défaut sur la page de tarification.
    displayedOnPricing: fee?.displayedOnPricing ?? true,
  }) as FeeFormValues;

interface FeeModalProps {
  tunnelId: string;
  /** Absent en création. */
  fraisTunnelId?: string;
  title: string;
  guarantees: { id: string; label: string }[];
  variables: ExpressionVariable[];
  onSuccess: () => void;
}

/**
 * Création et modification d'un frais du tunnel.
 *
 * Un seul formulaire couvre les deux cas, là où l'ancien back-office maintenait deux
 * modales de ~300 lignes de template chacune, dont les champs avaient fini par diverger.
 */
export const FeeModal: FC<FeeModalProps> = ({
  tunnelId,
  fraisTunnelId,
  title,
  guarantees,
  variables,
  onSuccess,
}) => {
  const formikRef = useRef<FormikProps<FeeFormValues>>(null);
  const { closeDialog } = useDialog();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditMode = Boolean(fraisTunnelId);

  const fetchFee = useCallback(
    () => funnelsAPI.getFunnelFee({ fraisTunnelId: fraisTunnelId as string }),
    [fraisTunnelId]
  );
  const fee = useAsyncResource(fetchFee, isEditMode);

  const fetchNatures = useCallback(() => funnelReferentialsAPI.getRemunerationNatures(), []);
  const natures = useAsyncResource<ReferentialItemDto[]>(fetchNatures);

  const initialValues = useMemo(() => buildInitialValues(fee.data), [fee.data]);

  const handleSubmit = async (values: FeeFormValues) => {
    setIsSubmitting(true);
    try {
      const command = {
        natureRemunerationId: values.natureRemunerationId,
        tarif: tarifFromFormValues(extractTarifValues('tarif', values)),
        useConnectedBroker: values.useConnectedBroker,
        // Le back ignore le bénéficiaire quand le courtier connecté est retenu :
        // on l'envoie à null pour éviter de conserver un titulaire fantôme.
        beneficiairePersonneId: values.useConnectedBroker ? null : values.beneficiairePersonneId,
        compteBancaireId: values.useConnectedBroker ? null : values.compteBancaireId || null,
        typePrelevement: values.typePrelevement,
        typeEcheance: values.typeEcheance,
        garantiesExclues: values.garantiesExclues,
        taxe: values.hasTaxe ? tarifFromFormValues(extractTarifValues('taxe', values)) : undefined,
        typeCommission: values.typeCommission,
        minimum: values.minimum === '' ? null : Number(values.minimum),
        maximum: values.maximum === '' ? null : Number(values.maximum),
        _default: values.valeurDefaut === '' ? null : Number(values.valeurDefaut),
        valeurs: parseNumberList(values.valeurs),
        displayedOnPricing: values.displayedOnPricing,
      };

      const response = isEditMode
        ? await funnelsAPI.updateFunnelFee({
            fraisTunnelId: fraisTunnelId as string,
            updateFraisTunnelCommand: command,
          })
        : await funnelsAPI.createFunnelFee({
            createFraisTunnelCommand: { ...command, tunnelId },
          });

      if (response.isSuccess) {
        toast.success(isEditMode ? 'Frais modifié' : 'Frais ajouté');
        onSuccess();
        closeDialog();
      } else {
        toast.error(response.errorMessage || "L'enregistrement a échoué");
      }
    } catch (error) {
      console.error('Enregistrement du frais impossible', error);
      // Le back explique la formule refusée ou la variable inconnue : on relaie son message.
      const parsed = await parseApiError(error);
      toast.error(parsed.message || "L'enregistrement a échoué");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ values }) => (
        <ModalShell
          title={title}
          subtitle="Rémunération prélevée en complément de la prime"
          isLoading={fee.isLoading || natures.isLoading}
          submit={{
            label: 'Sauvegarder',
            onClick: () => formikRef.current?.submitForm(),
            // Un frais existant en montant fixe TTC ne s'enregistre qu'après changement de mode.
            disabled: isSubmitting || isMontantTTCMode(values.tarifModeCalcul),
          }}
        >
          <Form>
            <ModalSection title="Nature et tarification" icon={<Calculator className="h-4 w-4" />}>
              <div className="flex flex-col gap-4">
                <FormikDropdown
                  name="natureRemunerationId"
                  label="Nature du frais"
                  required
                  items={(natures.data ?? []).map((nature) => ({
                    key: nature.id,
                    label: nature.libelle || nature.code || 'Sans libellé',
                  }))}
                  placeholder="Sélectionner une nature"
                />
                {isMontantTTCMode(values.tarifModeCalcul) && (
                  <p className="flex items-start gap-1.5 text-xs text-amber-700">
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                    <span>
                      Mode non supporté par le moteur de tarification : ressaisir le montant en HT
                    </span>
                  </p>
                )}
                <TarifFields
                  prefix="tarif"
                  label="Mode de calcul"
                  required
                  variables={variables}
                  allowMontantTTC={false}
                />
                <FormikSwitch name="displayedOnPricing" label="Afficher sur la page de tarification" />
              </div>
            </ModalSection>

            <ModalSection title="Bénéficiaire" icon={<UserRound className="h-4 w-4" />}>
              <BeneficiaryFields />
            </ModalSection>

            <ModalSection title="Prélèvement" icon={<Receipt className="h-4 w-4" />}>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <FormikDropdown
                  name="typePrelevement"
                  label="Type de prélèvement"
                  items={TYPE_PRELEVEMENT_OPTIONS.map((option) => ({
                    key: option.value,
                    label: option.label,
                  }))}
                />
                <FormikDropdown
                  name="typeEcheance"
                  label="Type d'échéance"
                  items={TYPE_ECHEANCE_OPTIONS.map((option) => ({
                    key: option.value,
                    label: option.label,
                  }))}
                />
              </div>
            </ModalSection>

            <ModalSection title="Taxe" icon={<Percent className="h-4 w-4" />} subtitle="Optionnelle">
              <FormikSwitch name="hasTaxe" label="Appliquer une taxe" />
              <TaxeFields />
            </ModalSection>

            <ModalSection
              title="Commission variable"
              icon={<Percent className="h-4 w-4" />}
              subtitle="Bornes proposées à la souscription"
            >
              <div className="flex flex-col gap-4">
                <FormikDropdown
                  name="typeCommission"
                  label="Mode de saisie"
                  items={TYPE_COMMISSION_OPTIONS.map((option) => ({
                    key: option.value,
                    label: option.label,
                  }))}
                />
                <CommissionBoundsFields />
              </div>
            </ModalSection>

            {guarantees.length > 0 && (
              <ModalSection
                title="Garanties exclues"
                icon={<Ban className="h-4 w-4" />}
                subtitle="Le frais ne s'applique pas aux garanties cochées"
              >
                <ExcludedGuaranteesField guarantees={guarantees} />
              </ModalSection>
            )}
          </Form>
        </ModalShell>
      )}
    </Formik>
  );
};

// ---------------------------------------------------------------- sous-blocs

/** Bénéficiaire et son compte bancaire, masqués si le courtier connecté encaisse. */
const BeneficiaryFields: FC = () => {
  const { values, setFieldValue } = useFormikContext<FeeFormValues>();

  // Tous les rôles sont éligibles, quelle que soit la nature du frais (TUN-018).
  const beneficiaries = useFeeBeneficiaryOptions();
  const bankAccounts = useBankAccountOptions(
    values.useConnectedBroker ? null : values.beneficiairePersonneId
  );

  // Présélection du compte par défaut du titulaire quand aucun n'est encore choisi.
  useEffect(() => {
    if (!values.compteBancaireId && bankAccounts.defaultAccountId) {
      void setFieldValue('compteBancaireId', bankAccounts.defaultAccountId);
    }
  }, [bankAccounts.defaultAccountId, values.compteBancaireId, setFieldValue]);

  return (
    <div className="flex flex-col gap-4">
      <div>
        <CheckboxComponent
          name="useConnectedBroker"
          label="Le courtier connecté est le bénéficiaire"
          checked={values.useConnectedBroker}
          onChange={(checked) => setFieldValue('useConnectedBroker', checked)}
        />
        <p className="ml-6 mt-0.5 text-xs text-gray-400">
          Si cochée, le courtier qui contractualise encaisse ce frais.
        </p>
      </div>

      {!values.useConnectedBroker && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormikDropdown
            name="beneficiairePersonneId"
            label="Bénéficiaire"
            required
            items={(beneficiaries.data ?? []).map((actor) => ({
              key: actor.key,
              label: actor.label,
              trailingLabel: actor.role,
            }))}
            placeholder={
              !values.natureRemunerationId
                ? "Sélectionner d'abord une nature de frais"
                : beneficiaries.isLoading
                  ? 'Chargement…'
                  : 'Sélectionner un bénéficiaire'
            }
            disabled={!values.natureRemunerationId}
          />
          <FormikDropdown
            name="compteBancaireId"
            label="Compte bancaire"
            items={bankAccounts.options}
            disabled={!values.beneficiairePersonneId}
            placeholder={
              !values.beneficiairePersonneId
                ? "Sélectionner d'abord un bénéficiaire"
                : bankAccounts.options.length
                  ? 'Sélectionner un compte'
                  : 'Aucun compte disponible'
            }
          />
        </div>
      )}
    </div>
  );
};

const TaxeFields: FC = () => {
  const { values } = useFormikContext<FeeFormValues>();
  if (!values.hasTaxe) return null;
  return (
    <div className="mt-4">
      <TarifFields
        prefix="taxe"
        label="Mode de calcul de la taxe"
        allowExpression={false}
        rateAsMultiplier
      />
    </div>
  );
};

const CommissionBoundsFields: FC = () => {
  const { values } = useFormikContext<FeeFormValues>();

  if (values.typeCommission === TypeCommission.List) {
    return (
      <FormikInputText
        name="valeurs"
        label="Valeurs proposées"
        placeholder="ex. 5 ; 7,5 ; 10"
      />
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
      <FormikInputText name="minimum" label="Minimum" type="number" />
      <FormikInputText name="valeurDefaut" label="Valeur par défaut" type="number" />
      <FormikInputText name="maximum" label="Maximum" type="number" />
    </div>
  );
};

const ExcludedGuaranteesField: FC<{ guarantees: { id: string; label: string }[] }> = ({
  guarantees,
}) => {
  const { values, setFieldValue } = useFormikContext<FeeFormValues>();

  const toggle = (guaranteeId: string, checked: boolean) => {
    const next = checked
      ? [...values.garantiesExclues, guaranteeId]
      : values.garantiesExclues.filter((id) => id !== guaranteeId);
    void setFieldValue('garantiesExclues', next);
  };

  return (
    <div className="grid max-h-56 grid-cols-1 gap-2 overflow-y-auto sm:grid-cols-2">
      {guarantees.map((guarantee) => (
        <CheckboxComponent
          key={guarantee.id}
          name={`exclusion-${guarantee.id}`}
          label={guarantee.label}
          checked={values.garantiesExclues.includes(guarantee.id)}
          onChange={(checked) => toggle(guarantee.id, checked)}
        />
      ))}
    </div>
  );
};
